import { WebClient } from "@injectables";
import { Node } from "@models";
import { ConnectionManager } from "./connection-manager";
import { DialogService } from "@app/old-ui/dialog/dialog.service";
import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { BaseNode, FoblexNode } from "./base-node/base-node";
import { DependentNode } from "./base-node/dependent-node";
import { PSDChartNode } from "@app/new-ui/flow-board/nodes/chart-node/chart-node.component";
import { DeviceNode } from "@app/new-ui/flow-board/nodes/device-node/device-node.component";
import { FragQueryService } from "src/injectables/frag-query.service";

export type FlowNode = DependentNode | BaseNode;

@Injectable({ providedIn: 'root' })
export class NodeManager {
    public trigger$ = new BehaviorSubject<void>(null);
    private readonly nodes = new BehaviorSubject<FlowNode[]>([]);
    public nodes$ = this.nodes.asObservable();
    public readonly nodeClient = this.webClient.model.node;
    public readonly nodeConnectionClient = this.webClient.model.nodeConnection;

    constructor(
        private readonly webClient: WebClient,
        private readonly dialog: DialogService,
        private connectionManager :ConnectionManager,
        private readonly fragQuery: FragQueryService,
    ) {}

    public addNode(node: FlowNode): void {
        const currentNodes = this.nodes.getValue();
        const exists = currentNodes.some(n => n.id === node.id);

        if (!exists) {
            this.nodes.next([...currentNodes, node]);
        } else {
            console.warn(`Node with id ${node.id} already exists. Skipping add.`);
        }
        console.log('added node',this.nodes.value );
    }

    public updateNodePosition(id: number, position: FoblexNode.Position): void {
        const nodes = this.nodes.getValue();
        const node = nodes.find(n => n.id === id);
        
        if (node) {
            // this.nodes.next([]);
            node.position = position;
    
            if (FoblexNode.isBaseNode(node)) {
                const dependentNodes = nodes.filter(n => 
                    FoblexNode.isDependentNode(n) && n.baseNode.id === node.id
                );
                dependentNodes.forEach(dep => {
                    if(FoblexNode.isDependentNode(dep)) {
                        dep.reCalculatePosition();
                    }
                });
            }
    
            this.nodes.next([...nodes]);
        }
    }

    public getNodes(): FlowNode[] {
        return this.nodes.getValue();
    }

    public removeAllNodes(): void {
        this.nodes.next([]);
    }

    public async removeNode(node: FlowNode): Promise<void> {
        if (FoblexNode.isDependentNode(node)) {
            this.nodes.next(this.nodes.getValue().filter(n => n.id !== node.id));
        } else {
            const confirmed = await this.dialog.confirm({ message: 'Are you sure you want to delete the Node?' });
            if (!confirmed) return;

            await this.dialog.await(this.performNodeAndConnectionsDelete(node));
        }
    }

    private async performNodeAndConnectionsDelete(node: FlowNode): Promise<void> {
        const ddNode = new Node();
        ddNode.id = node.id;
        await this.nodeClient.delete(ddNode);

        this.nodes.next(this.nodes.getValue().filter(n => FoblexNode.isDependentNode(n) ? n.baseNode.id !== node.id : n.id !== node.id));
        await this.connectionManager.RemoveConnectionOnNodeRemove(node);
        this.fragQuery.nodeTrigger$.next();
    }

    public addTemporaryPSDChartNode(baseNode: DeviceNode): FlowNode {
        const existingDependentNode = this.nodes.getValue().find((node) => FoblexNode.isDependentNode(node) && node.baseNode.id === baseNode.id);

        if (existingDependentNode) {
            console.warn("Temporary node for baseNodeId", baseNode.id, "already exists. Returning existing node.");
            return existingDependentNode;
        }
        const tempnode = new PSDChartNode(baseNode);

        this.addNode(tempnode);
        return tempnode;
    }

    public async getFreePosition() {
        const takenPositions = new Set<string>();
        const existingNodes = await this.nodeClient.search({})
        for (const n of existingNodes) {
            if (!n.position) continue;

            let pos;
            try {
                pos = JSON.parse(n.position);
            } catch {
                continue;
            }

            if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
                takenPositions.add(`${pos.x},${pos.y}`);
            }
        }

        let x = 50, y = 50;

        // Check for taken positions
        while (takenPositions.has(`${x},${y}`)) {
            x += 80;
            y += 80;

            // wrap-around to avoid large coordinates
            if (x > 300) x = 50;
            if (y > 300) y = 50;
        }
        return { x, y };
    }
}